import uniqBy from "lodash-es/uniqBy";
import type {
  AgentScope,
  AgentSecurityStatus,
  AttentionAgentIds,
  EnforcementReadinessCounts,
  NativePolicyState,
} from "@/api/temp/AgentEstateService";
import { ACTIVE } from "@/configs/constants";
import type { IAccount } from "@/types/api";

/**
 * Scope-and-count derivations shared by the Security Overview, the category
 * cards, the recommended-policy review and the application result. Pure - no
 * React, no i18n, no store, no network - so every surface renders the same
 * answer for the same population and a spec can pin each rule in isolation.
 *
 * Every count here is a count of DISTINCT canonical ids in a declared
 * population. Nothing sums overlapping populations, nothing turns "not loaded"
 * into zero. The contract table lives in
 * `docs/vision/AgentEstate_Count_Contract.md`.
 */

/* ------------------------------------------------------------ availability */

/** A value that may not exist yet: loading and failed are not zero. */
export type Availability<T> =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; value: T };

export const loadingAvailability = <T>(): Availability<T> => ({
  state: "loading",
});

export const readyAvailability = <T>(value: T): Availability<T> => ({
  state: "ready",
  value,
});

/** The value when ready; `undefined` for loading and failed - never `0`. */
export const availableValue = <T>(source: Availability<T>): T | undefined =>
  source.state === "ready" ? source.value : undefined;

/* -------------------------------------------------- set arithmetic, identity */

export const unionIds = (...sets: Array<Iterable<string>>): Set<string> => {
  const union = new Set<string>();
  sets.forEach(ids => {
    for (const id of ids) union.add(id);
  });
  return union;
};

/**
 * A tool id is only unique within its MCP service, so identity is namespaced.
 * Same format as `policyToolKey`, kept here so pure code needs no service
 * import at runtime.
 */
export const toolIdentity = (serviceId: string, toolId: string): string =>
  `${serviceId}::${toolId}`;

export type RecordDestination = "MCP_SERVER" | "MCP_TOOL" | "DATA_PLATFORM";

/**
 * The deterministic logical identity of a generated policy record: the same
 * environment, definition and target always agree, and two records that merely
 * share a title never collide.
 *
 * `resourceKey` is the server id, the namespaced tool key
 * (`toolIdentity`), or `${sourceId}:${controlId}` for a data-platform control.
 */
export const logicalRecordId = (record: {
  environmentId: string;
  definitionId: string;
  destination: RecordDestination;
  resourceKey: string;
}): string => {
  const kind =
    record.destination === "MCP_SERVER"
      ? "server"
      : record.destination === "MCP_TOOL"
        ? "tool"
        : "data";
  return `np:${record.environmentId}:${record.definitionId}:${kind}:${record.resourceKey}`;
};

/** FNV-1a: a small stable string hash for plan and review identities. */
export const fnv1a = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

/* ------------------------------------------------------ data-source inventory */

export interface DataSourceTypeInventory {
  type: string;
  total: number;
  secured: number;
  incomplete: number;
}

export type InventoryAccount = Pick<IAccount, "type" | "accountStatus">;

export interface DataSourceInventory<
  A extends InventoryAccount = InventoryAccount,
> {
  /** Every registered data source, typed or not. */
  total: number;
  secured: number;
  /** Every data source that is not ACTIVE - the Data tab's attention count. */
  incomplete: number;
  /** Rows with no `type`: counted in `total`, never grouped, never dropped. */
  untyped: number;
  byType: DataSourceTypeInventory[];
  /** The population that was counted - so a card built from it cannot disagree. */
  rows: readonly A[];
  /** `demo-snapshot` only when a bundled demo snapshot stood in for a tenant. */
  source: "live" | "demo-snapshot";
}

/**
 * The one data-source inventory: the strip, the tab badge, the overview tile
 * and the platform cards all read this, so they agree by construction.
 *
 * `accounts === undefined` means "not loaded", which is `loading` - an empty
 * array is a loaded, empty tenant and is `ready` with a total of 0. The live
 * list is always authoritative: `demoSnapshot` only stands in when the tenant
 * has no data sources of its own (unloaded or empty), and the result says so.
 */
export const selectDataSourceInventory = <A extends InventoryAccount>(input: {
  accounts: readonly A[] | undefined;
  demoSnapshot?: readonly A[];
}): Availability<DataSourceInventory<A>> => {
  const live = input.accounts?.length ? input.accounts : undefined;
  const rows = live ?? input.demoSnapshot ?? input.accounts;
  if (!rows) return loadingAvailability();

  const byType = new Map<string, DataSourceTypeInventory>();
  let untyped = 0;
  let secured = 0;
  rows.forEach(row => {
    const isSecured = row.accountStatus === ACTIVE;
    if (isSecured) secured += 1;
    if (!row.type) {
      untyped += 1;
      return;
    }
    const group = byType.get(row.type) ?? {
      type: row.type,
      total: 0,
      secured: 0,
      incomplete: 0,
    };
    group.total += 1;
    if (isSecured) group.secured += 1;
    else group.incomplete += 1;
    byType.set(row.type, group);
  });

  return readyAvailability({
    total: rows.length,
    secured,
    incomplete: rows.length - secured,
    untyped,
    byType: [...byType.values()],
    rows,
    source: !live && input.demoSnapshot ? "demo-snapshot" : "live",
  });
};

/* ----------------------------------------------------------------- attention */

export interface AttentionIds {
  /** NOT_SECURE. */
  policyGap?: Iterable<string>;
  /** Enforcement readiness SETUP_REQUIRED or ATTENTION_REQUIRED. */
  setupGap?: Iterable<string>;
  /** NOT_EVALUATED. */
  notEvaluated?: Iterable<string>;
}

export interface AttentionCounts {
  policyGap: number;
  setupGap: number;
  notEvaluated: number;
  /** Enforcement readiness UNKNOWN - visible, never a confirmed gap. */
  unknownReadiness: number;
}

export interface AttentionSummary {
  /** `true` when `count` is the exact union of distinct agent ids. */
  exact: boolean;
  /**
   * Agents needing attention: policy gap OR setup gap OR not evaluated. Exact
   * when ids were available; otherwise the lower bound (the largest bucket).
   */
  count: number;
  /** Sum of the buckets - the most it could be. Equals `count` when exact. */
  upperBound: number;
  buckets: AttentionCounts;
  /** Unknown readiness, reported beside the total and never added to it. */
  unknownReadiness: number;
}

/** Setup gap = readiness that is known to be incomplete. Unknown is excluded. */
export const setupGapCount = (counts: EnforcementReadinessCounts): number =>
  counts.setupRequired + counts.attentionRequired;

/**
 * Attention is the union of the agents that qualify under any rule, never the
 * sum of the per-rule counts: an agent with a policy gap AND a setup gap is one
 * agent. Without agent ids only bounds can be known, and they are reported as
 * bounds rather than as a made-up union.
 */
export const summarizeAttention = (
  ids: AttentionIds | undefined,
  counts: AttentionCounts,
): AttentionSummary => {
  const { policyGap, setupGap, notEvaluated } = ids ?? {};
  const upperBound = counts.policyGap + counts.setupGap + counts.notEvaluated;
  if (policyGap && setupGap && notEvaluated) {
    const union = unionIds(policyGap, setupGap, notEvaluated);
    return {
      exact: true,
      count: union.size,
      upperBound: union.size,
      buckets: counts,
      unknownReadiness: counts.unknownReadiness,
    };
  }
  return {
    exact: false,
    count: Math.max(counts.policyGap, counts.setupGap, counts.notEvaluated),
    upperBound,
    buckets: counts,
    unknownReadiness: counts.unknownReadiness,
  };
};

/* -------------------------------------------------------------- protection */

export interface ProtectionSummary {
  /** Agents missing any TrustLogix control. A lower bound when `!exact`. */
  unprotected: number;
  /** Policy-secure AND enforcement READY. An upper bound when `!exact`. */
  protected: number;
  exact: boolean;
}

/**
 * Protected vs Unprotected for a group or the whole estate. An agent is
 * Protected only when it is policy-secure AND on a READY enforcement path;
 * every other agent - unknown readiness included, since protection cannot be
 * claimed for it - is Unprotected.
 *
 * Exact from the server's `protectedCount`, else from the distinct agent ids;
 * otherwise only a lower bound (the larger of the two gaps) can be known, and
 * it is reported as one rather than as a made-up union.
 */
export const summarizeProtection = (source: {
  totalAgents: number;
  notSecureCount: number;
  notEvaluatedCount: number;
  enforcement: EnforcementReadinessCounts;
  protectedCount?: number;
  attentionIds?: AttentionAgentIds;
}): ProtectionSummary => {
  const total = source.totalAgents;
  const clamp = (count: number) => Math.min(total, Math.max(0, count));
  const exactly = (unprotected: number): ProtectionSummary => ({
    unprotected: clamp(unprotected),
    protected: total - clamp(unprotected),
    exact: true,
  });
  if (source.protectedCount !== undefined)
    return exactly(total - source.protectedCount);
  const ids = source.attentionIds;
  if (ids)
    return exactly(
      unionIds(
        ids.policyGap,
        ids.notEvaluated,
        ids.setupGap,
        ids.unknownReadiness,
      ).size,
    );
  const policyGap = source.notSecureCount + source.notEvaluatedCount;
  const enforcementGap =
    setupGapCount(source.enforcement) + source.enforcement.unknown;
  const unprotected = clamp(Math.max(policyGap, enforcementGap));
  return { unprotected, protected: total - unprotected, exact: false };
};

/**
 * Distinct agents across groups that may overlap (risk cards, platforms
 * reached). Never add the per-group totals.
 */
export const unionMembership = (
  groups: ReadonlyArray<{ agentIds: readonly string[] }>,
): { total: number; ids: Set<string> } => {
  const ids = unionIds(...groups.map(group => group.agentIds));
  return { total: ids.size, ids };
};

/* ------------------------------------------------------------- review cohort */

export type NotTargetedReason = "NOT_APPLICABLE" | "ALREADY_ASSIGNED";

export type CohortMembership = "TARGETED" | NotTargetedReason | "UNRESOLVED";

export interface CohortAgentInput {
  id: string;
  /** Selected recommendations whose modelled targets resolve for this agent. */
  applicablePolicyIds: readonly string[];
  /** Applicable AND not yet satisfied for this agent. */
  pendingPolicyIds: readonly string[];
  /** The relationship record could not be read: neither targeted nor excluded. */
  applicabilityUnresolved?: boolean;
}

export interface ReviewCohort {
  /** How review was opened: from one agent, or from a category/filter/list. */
  entry: "SINGLE_AGENT" | "MULTI_AGENT";
  /** What the source cohort is: a category, a filter, chosen agents, or all. */
  scopeKind: AgentScope["kind"];
  /** Distinct agents the originating category, filter or page represents. */
  sourceAgentIds: string[];
  /** Source agents, narrowed only by an explicit agent selection. */
  candidateAgentIds: string[];
  /** Candidates to which at least one selected recommendation newly applies. */
  targetedAgentIds: string[];
  /** Candidates no selected recommendation newly applies to, with a reason. */
  notTargeted: Array<{ agentId: string; reason: NotTargetedReason }>;
  /** Candidates whose applicability could not be established. */
  applicabilityUnresolved: string[];
  inspectedAgentId?: string;
  selectedRecommendationIds: string[];
  /** `user` only when the user chose the recommendations; drives the wording. */
  recommendationSelection: "user" | "default" | "none";
  /** True when the user narrowed the source cohort to chosen agents. */
  agentSelectionActive: boolean;
  /** Per-recommendation relationships, so nothing applies to every agent. */
  perRecommendation: Record<
    string,
    { applicableIds: string[]; pendingIds: string[]; assignedIds: string[] }
  >;
  /**
   * What the targeted agents are projected to become if every operation
   * succeeds. Attached by whoever can run the coverage evaluator over a
   * hypothetical state; absent when that has not been done.
   */
  expectedCoverageChanges?: ExpectedCoverageChanges;
}

export const buildReviewCohort = (input: {
  entry: ReviewCohort["entry"];
  scopeKind?: AgentScope["kind"];
  /** Every agent of the originating scope, in display order. */
  agents: readonly CohortAgentInput[];
  selectedRecommendationIds: readonly string[];
  recommendationSelection: ReviewCohort["recommendationSelection"];
  /** Present only when the user explicitly narrowed the agents. */
  selectedAgentIds?: readonly string[];
  /**
   * The scope itself is the user's own agent selection (rows ticked in a
   * list), so "selected" is the right word for it.
   */
  agentSelectionActive?: boolean;
  requestedInspectedId?: string;
}): ReviewCohort => {
  const sourceAgents = uniqBy(input.agents, agent => agent.id);
  const sourceAgentIds = sourceAgents.map(agent => agent.id);
  const narrowed = input.selectedAgentIds
    ? new Set(input.selectedAgentIds)
    : undefined;
  const candidates = narrowed
    ? sourceAgents.filter(agent => narrowed.has(agent.id))
    : sourceAgents;

  const targetedAgentIds: string[] = [];
  const notTargeted: ReviewCohort["notTargeted"] = [];
  const applicabilityUnresolved: string[] = [];
  candidates.forEach(agent => {
    if (agent.pendingPolicyIds.length > 0) {
      targetedAgentIds.push(agent.id);
    } else if (
      agent.applicabilityUnresolved &&
      agent.applicablePolicyIds.length === 0
    ) {
      applicabilityUnresolved.push(agent.id);
    } else {
      notTargeted.push({
        agentId: agent.id,
        reason:
          agent.applicablePolicyIds.length > 0
            ? "ALREADY_ASSIGNED"
            : "NOT_APPLICABLE",
      });
    }
  });

  const selectedRecommendationIds = [
    ...new Set(input.selectedRecommendationIds),
  ];
  const perRecommendation: ReviewCohort["perRecommendation"] = {};
  selectedRecommendationIds.forEach(policyId => {
    const applicableIds = candidates
      .filter(agent => agent.applicablePolicyIds.includes(policyId))
      .map(agent => agent.id);
    const pending = new Set(
      candidates
        .filter(agent => agent.pendingPolicyIds.includes(policyId))
        .map(agent => agent.id),
    );
    perRecommendation[policyId] = {
      applicableIds,
      pendingIds: applicableIds.filter(id => pending.has(id)),
      assignedIds: applicableIds.filter(id => !pending.has(id)),
    };
  });

  const cohort: ReviewCohort = {
    entry: input.entry,
    scopeKind: input.scopeKind ?? "ALL",
    sourceAgentIds,
    candidateAgentIds: candidates.map(agent => agent.id),
    targetedAgentIds,
    notTargeted,
    applicabilityUnresolved,
    selectedRecommendationIds,
    recommendationSelection: input.recommendationSelection,
    agentSelectionActive: !!narrowed || !!input.agentSelectionActive,
    perRecommendation,
  };
  cohort.inspectedAgentId = resolveInspectedAgent(
    cohort,
    input.requestedInspectedId,
  );
  return cohort;
};

/**
 * The agent the review is inspecting: the requested one when it is targeted,
 * else the first targeted agent, else none. Inspecting never changes scope, and
 * a stale or out-of-scope id can never survive.
 */
export const resolveInspectedAgent = (
  cohort: Pick<ReviewCohort, "targetedAgentIds">,
  requested?: string,
): string | undefined =>
  requested && cohort.targetedAgentIds.includes(requested)
    ? requested
    : cohort.targetedAgentIds[0];

export type CohortView = "targeted" | "excluded" | "all";

export interface CohortRow {
  agentId: string;
  membership: CohortMembership;
}

/** Candidate order, filtered by membership - the table's row population. */
export const cohortRows = (
  cohort: ReviewCohort,
  view: CohortView,
): CohortRow[] => {
  const targeted = new Set(cohort.targetedAgentIds);
  const unresolved = new Set(cohort.applicabilityUnresolved);
  const reasons = new Map(
    cohort.notTargeted.map(item => [item.agentId, item.reason] as const),
  );
  const rows = cohort.candidateAgentIds.map((agentId): CohortRow => {
    if (targeted.has(agentId)) return { agentId, membership: "TARGETED" };
    if (unresolved.has(agentId)) return { agentId, membership: "UNRESOLVED" };
    return { agentId, membership: reasons.get(agentId) ?? "NOT_APPLICABLE" };
  });
  if (view === "all") return rows;
  return rows.filter(row =>
    view === "targeted"
      ? row.membership === "TARGETED"
      : row.membership !== "TARGETED",
  );
};

/* ---------------------------------------------------------- expected coverage */

export type CoverageChange =
  | "NEWLY_COVERED"
  | "IMPROVED"
  | "UNCHANGED"
  | "UNKNOWN"
  | "REGRESSED";

export interface CoverageSnapshot {
  status: AgentSecurityStatus;
  /** Policy gaps still open for this agent. */
  gapCount: number;
}

/**
 * Compares an agent before and after a (hypothetical or confirmed)
 * application. An agent that cannot be assessed on either side is UNKNOWN - it
 * is never quietly counted as unchanged.
 */
export const classifyCoverageChange = (
  before: CoverageSnapshot,
  after: CoverageSnapshot | undefined,
): CoverageChange => {
  if (
    !after ||
    before.status === "NOT_EVALUATED" ||
    after.status === "NOT_EVALUATED"
  )
    return "UNKNOWN";
  if (before.status === "SECURE")
    return after.status === "SECURE" ? "UNCHANGED" : "REGRESSED";
  if (after.status === "SECURE") return "NEWLY_COVERED";
  if (after.gapCount < before.gapCount) return "IMPROVED";
  return after.gapCount > before.gapCount ? "REGRESSED" : "UNCHANGED";
};

export interface ExpectedCoverageChanges {
  newlyCovered: number;
  improved: number;
  unchanged: number;
  unknown: number;
  regressed: number;
  agentIdsByChange: Record<CoverageChange, string[]>;
}

export const tallyCoverageChanges = (
  entries: ReadonlyArray<{
    agentId: string;
    before: CoverageSnapshot;
    after?: CoverageSnapshot;
  }>,
): ExpectedCoverageChanges => {
  const agentIdsByChange: Record<CoverageChange, string[]> = {
    NEWLY_COVERED: [],
    IMPROVED: [],
    UNCHANGED: [],
    UNKNOWN: [],
    REGRESSED: [],
  };
  uniqBy(entries, entry => entry.agentId).forEach(entry =>
    agentIdsByChange[classifyCoverageChange(entry.before, entry.after)].push(
      entry.agentId,
    ),
  );
  return {
    newlyCovered: agentIdsByChange.NEWLY_COVERED.length,
    improved: agentIdsByChange.IMPROVED.length,
    unchanged: agentIdsByChange.UNCHANGED.length,
    unknown: agentIdsByChange.UNKNOWN.length,
    regressed: agentIdsByChange.REGRESSED.length,
    agentIdsByChange,
  };
};

/* ---------------------------------------------------------- application plan */

export type PlanOperation =
  | "CREATE"
  | "UPDATE"
  | "UNCHANGED"
  | "SKIPPED_OVERRIDE"
  | "PENDING_CLASSIFICATION";

export interface PlanRecordInput {
  /** `logicalRecordId` - the dedupe key. */
  id: string;
  definitionId: string;
  destination: RecordDestination;
  /** Targeted agents that need this record. */
  dependentAgentIds: readonly string[];
  /** Whether the record's current existence could be read. */
  existenceKnown: boolean;
  /** Present when the record already exists. */
  existing?: {
    /** Agents the record already serves. */
    dependentAgentIds: readonly string[];
    state?: NativePolicyState;
    /** Carries an individual override the plan must not overwrite. */
    overridden?: boolean;
  };
}

export interface PlanRecord {
  id: string;
  definitionIds: string[];
  destination: RecordDestination;
  operation: PlanOperation;
  /** Targeted agents that depend on this record. */
  dependentAgentIds: string[];
  /** Agents it already serves that are outside the reviewed cohort. */
  outsideScopeAgentIds: string[];
}

export interface ApplicationPlan {
  records: PlanRecord[];
  targetedAgentIds: string[];
  definitionIds: string[];
  /** Distinct agent-recommendation bindings the plan creates. */
  assignments: Array<{ agentId: string; policyId: string }>;
  units: {
    recommendedDefinitions: number;
    targetedAgents: number;
    assignments: number;
    /** MCP server + tool records managed in the TrustLogix policy engine. */
    trustlogixRecords: number;
    mcpServerRecords: number;
    mcpToolRecords: number;
    /** Native controls deployed to data platforms. */
    nativeControls: number;
    /** trustlogixRecords + nativeControls, across both destinations. */
    generatedChanges: number;
  };
  operations: {
    create: number;
    update: number;
    unchanged: number;
    skipped: number;
    pending: number;
  };
  /** Existing records this plan would also change for agents outside scope. */
  sharedImpact: { recordIds: string[]; agentIds: string[] };
  /** Identity of the plan's content: a changed plan has a different value. */
  fingerprint: string;
}

const classifyRecord = (
  record: PlanRecordInput,
  dependents: ReadonlySet<string>,
): PlanOperation => {
  if (!record.existenceKnown) return "PENDING_CLASSIFICATION";
  if (!record.existing) return "CREATE";
  if (record.existing.overridden) return "SKIPPED_OVERRIDE";
  const served = new Set(record.existing.dependentAgentIds);
  const adds = [...dependents].some(id => !served.has(id));
  // A failed record is retried rather than trusted as already in place.
  return adds || record.existing.state === "FAILED" ? "UPDATE" : "UNCHANGED";
};

/**
 * One plan drives review totals, execution and result reconciliation.
 * Records are deduplicated by logical id (never by title), so a record shared
 * by several agents or definitions is one record, and its dependents are the
 * union of everyone who needs it.
 */
export const buildApplicationPlan = (input: {
  records: readonly PlanRecordInput[];
  definitionIds: readonly string[];
  targetedAgentIds: readonly string[];
  /** The reviewed cohort - who may be affected without it being reviewed. */
  cohortAgentIds: readonly string[];
  assignments: ReadonlyArray<{ agentId: string; policyId: string }>;
}): ApplicationPlan => {
  const merged = new Map<
    string,
    {
      input: PlanRecordInput;
      definitionIds: Set<string>;
      dependents: Set<string>;
      overridden: boolean;
    }
  >();
  input.records.forEach(record => {
    const entry = merged.get(record.id);
    if (entry) {
      entry.definitionIds.add(record.definitionId);
      record.dependentAgentIds.forEach(id => entry.dependents.add(id));
      entry.overridden ||= !!record.existing?.overridden;
    } else {
      merged.set(record.id, {
        input: record,
        definitionIds: new Set([record.definitionId]),
        dependents: new Set(record.dependentAgentIds),
        overridden: !!record.existing?.overridden,
      });
    }
  });

  const cohort = new Set(input.cohortAgentIds);
  const records: PlanRecord[] = [...merged.values()].map(entry => {
    const base = entry.overridden
      ? {
          ...entry.input,
          existing: { ...entry.input.existing!, overridden: true },
        }
      : entry.input;
    return {
      id: base.id,
      definitionIds: [...entry.definitionIds],
      destination: base.destination,
      operation: classifyRecord(base, entry.dependents),
      dependentAgentIds: [...entry.dependents],
      outsideScopeAgentIds: (base.existing?.dependentAgentIds ?? []).filter(
        id => !cohort.has(id),
      ),
    };
  });

  const assignments = uniqBy(
    input.assignments,
    item => `${item.agentId}|${item.policyId}`,
  );
  const count = (destination: RecordDestination) =>
    records.filter(record => record.destination === destination).length;
  const operation = (name: PlanOperation) =>
    records.filter(record => record.operation === name).length;
  const mcpServerRecords = count("MCP_SERVER");
  const mcpToolRecords = count("MCP_TOOL");
  const nativeControls = count("DATA_PLATFORM");
  const shared = records.filter(
    record =>
      record.operation !== "SKIPPED_OVERRIDE" &&
      record.outsideScopeAgentIds.length > 0,
  );

  return {
    records,
    targetedAgentIds: [...new Set(input.targetedAgentIds)],
    definitionIds: [...new Set(input.definitionIds)],
    assignments,
    units: {
      recommendedDefinitions: new Set(input.definitionIds).size,
      targetedAgents: new Set(input.targetedAgentIds).size,
      assignments: assignments.length,
      trustlogixRecords: mcpServerRecords + mcpToolRecords,
      mcpServerRecords,
      mcpToolRecords,
      nativeControls,
      generatedChanges: records.length,
    },
    operations: {
      create: operation("CREATE"),
      update: operation("UPDATE"),
      unchanged: operation("UNCHANGED"),
      skipped: operation("SKIPPED_OVERRIDE"),
      pending: operation("PENDING_CLASSIFICATION"),
    },
    sharedImpact: {
      recordIds: shared.map(record => record.id),
      agentIds: [...unionIds(...shared.map(r => r.outsideScopeAgentIds))],
    },
    fingerprint: String(
      fnv1a(
        JSON.stringify(
          records
            .map(record => [
              record.id,
              record.operation,
              [...record.dependentAgentIds].sort(),
            ])
            .sort((left, right) => (left[0] < right[0] ? -1 : 1)),
        ),
      ),
    ),
  };
};

export type PlanOperationCounts = ApplicationPlan["operations"];

/**
 * The plan's operation counts, for the whole plan or for one definition's
 * records. A record shared by several definitions counts for each of them.
 */
export const operationCounts = (
  plan: ApplicationPlan,
  definitionId?: string,
): PlanOperationCounts => {
  const counts: PlanOperationCounts = {
    create: 0,
    update: 0,
    unchanged: 0,
    skipped: 0,
    pending: 0,
  };
  plan.records
    .filter(
      record => !definitionId || record.definitionIds.includes(definitionId),
    )
    .forEach(record => {
      if (record.operation === "CREATE") counts.create += 1;
      else if (record.operation === "UPDATE") counts.update += 1;
      else if (record.operation === "UNCHANGED") counts.unchanged += 1;
      else if (record.operation === "SKIPPED_OVERRIDE") counts.skipped += 1;
      else counts.pending += 1;
    });
  return counts;
};

/* -------------------------------------------------------- outcome reconciling */

export type OperationOutcome =
  | "CREATED"
  | "UPDATED"
  | "UNCHANGED"
  | "FAILED"
  | "SKIPPED"
  | "PENDING";

export type AgentOutcome =
  | "SUCCESS"
  | "PARTIAL"
  | "FAILED"
  | "UNCHANGED"
  | "PENDING"
  | "SKIPPED";

export interface OutcomeReport {
  operations: Array<{
    recordId: string;
    planned: PlanOperation;
    outcome: OperationOutcome;
  }>;
  /** Sums to the plan's record count. */
  totals: Record<OperationOutcome, number>;
  createdByDestination: Record<RecordDestination, number>;
  agents: Array<{
    agentId: string;
    outcome: AgentOutcome;
    failedRecordIds: string[];
    pendingRecordIds: string[];
  }>;
  /** Agent-recommendation pairs whose every record is confirmed in place. */
  satisfiedBindings: Array<{ agentId: string; policyId: string }>;
}

const SATISFIED: ReadonlySet<OperationOutcome> = new Set([
  "CREATED",
  "UPDATED",
  "UNCHANGED",
  "SKIPPED",
]);

const recordOutcome = (
  record: PlanRecord,
  state: NativePolicyState | undefined,
  existedBefore: ReadonlySet<string> | undefined,
): OperationOutcome => {
  if (record.operation === "SKIPPED_OVERRIDE") return "SKIPPED";
  if (record.operation === "UNCHANGED") return "UNCHANGED";
  if (state === "FAILED") return "FAILED";
  if (state !== "DEPLOYED") return "PENDING";
  if (record.operation === "CREATE") return "CREATED";
  if (record.operation === "UPDATE") return "UPDATED";
  // Classification was unavailable when the plan was made: only a caller that
  // knows what existed before can settle it.
  if (!existedBefore) return "PENDING";
  return existedBefore.has(record.id) ? "UPDATED" : "CREATED";
};

const agentOutcome = (outcomes: OperationOutcome[]): AgentOutcome => {
  if (outcomes.length === 0) return "SKIPPED";
  const done = outcomes.filter(
    outcome =>
      outcome === "CREATED" || outcome === "UPDATED" || outcome === "UNCHANGED",
  ).length;
  if (outcomes.includes("FAILED")) return done > 0 ? "PARTIAL" : "FAILED";
  if (outcomes.includes("PENDING")) return "PENDING";
  if (outcomes.every(outcome => outcome === "SKIPPED")) return "SKIPPED";
  if (outcomes.some(outcome => outcome === "CREATED" || outcome === "UPDATED"))
    return "SUCCESS";
  return "UNCHANGED";
};

/**
 * Maps confirmed record states back through the plan's dependents. A record
 * whose state is missing is PENDING, not FAILED: unknown is not a verdict. A
 * failed shared record fails every agent that depends on it.
 */
export const reconcileOutcome = (
  plan: ApplicationPlan,
  states: ReadonlyMap<string, NativePolicyState | undefined>,
  existedBefore?: ReadonlySet<string>,
): OutcomeReport => {
  const operations = plan.records.map(record => ({
    recordId: record.id,
    planned: record.operation,
    outcome: recordOutcome(record, states.get(record.id), existedBefore),
  }));
  const outcomeById = new Map(
    operations.map(item => [item.recordId, item.outcome] as const),
  );

  const totals: Record<OperationOutcome, number> = {
    CREATED: 0,
    UPDATED: 0,
    UNCHANGED: 0,
    FAILED: 0,
    SKIPPED: 0,
    PENDING: 0,
  };
  const createdByDestination: Record<RecordDestination, number> = {
    MCP_SERVER: 0,
    MCP_TOOL: 0,
    DATA_PLATFORM: 0,
  };
  plan.records.forEach(record => {
    const outcome = outcomeById.get(record.id)!;
    totals[outcome] += 1;
    if (outcome === "CREATED") createdByDestination[record.destination] += 1;
  });

  const agents = plan.targetedAgentIds.map(agentId => {
    const own = plan.records.filter(record =>
      record.dependentAgentIds.includes(agentId),
    );
    const outcomes = own.map(record => outcomeById.get(record.id)!);
    return {
      agentId,
      outcome: agentOutcome(outcomes),
      failedRecordIds: own
        .filter(record => outcomeById.get(record.id) === "FAILED")
        .map(record => record.id),
      pendingRecordIds: own
        .filter(record => outcomeById.get(record.id) === "PENDING")
        .map(record => record.id),
    };
  });

  const satisfiedBindings = plan.assignments.filter(({ agentId, policyId }) => {
    const own = plan.records.filter(
      record =>
        record.dependentAgentIds.includes(agentId) &&
        record.definitionIds.includes(policyId),
    );
    return (
      own.length > 0 &&
      own.every(record => SATISFIED.has(outcomeById.get(record.id)!))
    );
  });

  return {
    operations,
    totals,
    createdByDestination,
    agents,
    satisfiedBindings,
  };
};
